import { useEffect, useRef, useState } from "preact/hooks";
import {
  contactCardFileName,
  deleteWithUndo,
  flushBlobs,
  photoHint,
  required,
  showToast,
  storePhotos,
  styled,
  takenNames,
  ToucanButton,
  ToucanChip,
  ToucanChipMenu,
  ToucanChipRow,
  ToucanDangerZone,
  ToucanNewThingDialog,
  ToucanPhotoRow,
  toucanPill,
  usePhotoDrop,
} from "toucan";
import { MyQr } from "../components/MyQr.tsx";
import {
  addPerson,
  addTag,
  deletePerson,
  personMastery,
  personReviews,
  resetPersonReviews,
  restoreReviews,
  sortedTags,
  tagLabel,
  toggleId,
  updatePerson,
} from "../model.ts";
import { go, listPath } from "../routes.ts";
import { store } from "../state.ts";
import { card, contactLink, isAndroid, saveToContacts } from "../vcard.ts";
import type { Person, PersonInput } from "../model.ts";

const Page = styled("section")`
  min-height: 100%;
  /* The same warm canvas the browse list sits on. */
  background: var(--toucan-canvas);

  /* The name and the photos down one side and the QR card up the other, each
     as tall as the other, so the code costs the form no height of its own. */
  .person-top {
    display: flex;
    gap: 0.75rem;
    align-items: stretch;
  }

  .person-fields {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
  }

  /* The field is as wide as a number, and the action sits right after it
     rather than at the far end of the row, where a Save button would be. A
     row too narrow for both puts the action underneath. */
  .person-phone-row {
    flex-wrap: wrap;
  }

  .person-phone-row input {
    flex: 0 1 18ch;
  }

  /* Tucked right under the photo row, quieter than the form's usual rhythm. */
  .form-photos-hint {
    margin: -0.7rem 0 0;
  }

  /* The form is the page's column; the section around it paints the canvas
     behind it and holds the room either side. */
  form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 0;
  }
`;

/**
 * The action on the phone row, named for the phone's book rather than the
 * app's: the one pill on a form that saves as it goes, it would otherwise read
 * as Save.
 */
const ADD_TO_CONTACTS = "Add to phone contacts";

/** Everything the form holds, and what the store is written from. */
interface Draft {
  name: string;
  note: string;
  phone: string;
  tagIds: string[];
  photos: string[];
}

/** The draft a page opens with: the person being edited, or a blank capture. */
function opening(person: Person | null, activeTagIds: string[]): Draft {
  if (person === null) {
    return {
      name: "",
      note: "",
      phone: "",
      tagIds: [...activeTagIds],
      photos: [],
    };
  }
  return {
    name: person.name,
    note: person.note ?? "",
    phone: person.phone ?? "",
    tagIds: [...person.tagIds],
    photos: [...person.photos],
  };
}

/**
 * The draft and the store, and when one reaches the other.
 *
 * The page holds the whole draft, so a sync landing mid-edit never wipes what
 * has been typed. Discrete choices — a tag, a photo — are written as they are
 * made; typing is written when the field is left, and on the way off the page
 * for a line that was never left.
 *
 * Someone new becomes a person as soon as there is something to file: a name,
 * or a note about where you met, which files them as Unnamed until they have
 * one. A form left with neither writes nothing — there was nobody to save.
 */
function useDraft(initial: Person | null, activeTagIds: string[]) {
  const [person, setPerson] = useState(initial);
  const [draft, setDraft] = useState(() => opening(initial, activeTagIds));
  // The unmount pass reads the latest of both without an effect per render.
  const live = useRef({ person, draft, dirty: false });
  live.current = { ...live.current, person, draft };

  const write = (next: Draft): void => {
    live.current.dirty = false;
    const held = live.current.person;
    const note = next.note.trim();
    // A name cleared on someone who has one keeps the one they have, because
    // an emptied field is a slip.
    const kept =
      next.name.trim() === "" ? (held?.name ?? "") : next.name.trim();
    const data: PersonInput = {
      // Somebody filed under a note alone is still a card in the list, and a
      // card with no heading and no initials says nothing about who it is.
      name: kept === "" && note !== "" ? "Unnamed" : kept,
      tagIds: [...next.tagIds],
      photos: [...next.photos],
    };
    if (note !== "") {
      data.note = note;
    }
    const phone = next.phone.trim();
    if (phone !== "") {
      data.phone = phone;
    }
    // A trimmed or restored name is what the field shows from here on.
    if (data.name !== next.name) {
      setDraft({ ...next, name: data.name });
    }
    if (held !== null) {
      updatePerson(held, data);
    } else if (data.name !== "") {
      const made = addPerson(store(), data);
      live.current.person = made;
      setPerson(made);
      // Android may kill the app behind the gallery picker, and the reload has
      // to open them rather than a blank form. Replacing the entry fires no
      // hashchange, so the form stays mounted.
      history.replaceState(
        null,
        "",
        `${location.pathname}${location.search}#/edit/${made.id}`,
      );
    }
    void flushBlobs();
  };

  useEffect(() => {
    return () => {
      if (live.current.dirty) {
        write(live.current.draft);
      }
    };
  }, []);

  return {
    person,
    draft,
    /** The current draft, for the handlers that resume after an await. */
    latest: (): Draft => live.current.draft,
    /** A line being typed: kept on the page until the field is left. */
    type: (part: Partial<Draft>): void => {
      live.current.dirty = true;
      setDraft((current) => ({ ...current, ...part }));
    },
    /** A choice made, or a field left, written to the store at once. */
    save: (part: Partial<Draft>): void => {
      const next = { ...live.current.draft, ...part };
      setDraft(next);
      write(next);
    },
  };
}

/**
 * Delete a person, and their review history with them, but offer both straight
 * back as an undo rather than asking first.
 */
function deletePersonWithUndo(person: Person): void {
  const removed = personReviews(store(), person.id);
  deleteWithUndo(
    store().people,
    person,
    `Deleted ${person.name}`,
    () => {
      deletePerson(store(), person.id);
      go(listPath());
    },
    () => {
      restoreReviews(store(), removed);
    },
  );
}

/** Clear one person's review history, offering it straight back as an undo. */
function resetReviews(person: Person): void {
  // Reset at once, offer undo — the same "don't ask, let me take it back"
  // model as deleting a person or a tag, so there's no heavy confirm to clear.
  const removed = personReviews(store(), person.id);
  resetPersonReviews(store(), person.id);
  // Short message: the form already makes clear whose progress it is.
  showToast("Review progress reset", () => {
    restoreReviews(store(), removed);
  });
}

/** What can be undone about a person who exists: their reviews, and them. */
function DangerZone({ person }: { person: Person }) {
  const mastery = personMastery(store(), person.id);
  return (
    <ToucanDangerZone>
      {mastery !== null && (
        <ToucanButton
          variant="warning"
          onClick={() => {
            resetReviews(person);
          }}
        >
          Reset review progress
        </ToucanButton>
      )}
      <ToucanButton
        variant="danger"
        onClick={() => {
          deletePersonWithUndo(person);
        }}
      >
        Delete person
      </ToucanButton>
    </ToucanDangerZone>
  );
}

/**
 * The capture/edit form: name, tags, photos and a note. Everything except the
 * name is optional so mid-conversation capture takes seconds. Photos arrive
 * from the picker, the camera, a paste or a drop, and are downscaled into the
 * blob store on the way in.
 *
 * `person` is the one being edited, or `null` while capturing someone new.
 * There is no Save: the form writes as it is filled in, and the back arrow is
 * all there is to leave it by.
 */
export function FormPage({
  person: initial,
  activeTagIds,
}: {
  person: Person | null;
  activeTagIds: string[];
}) {
  const { person, draft, latest, type, save } = useDraft(initial, activeTagIds);
  const [hint, setHint] = useState("");
  const [newTag, setNewTag] = useState(false);
  // Somebody new is a name first: the caret goes in the field the moment the
  // form is on screen. `autofocus` alone only counts on a page load, and this
  // form arrives on a route change.
  const nameField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (initial === null) {
      required(nameField.current, "name field").focus();
    }
  }, [initial]);

  /** Store a batch pasted or dropped into the form, and show it. */
  async function addFiles(files: Iterable<File | Blob>): Promise<void> {
    const stored = await storePhotos(files);
    setHint(photoHint(stored));
    save({ photos: [...latest().photos, ...stored.hashes] });
  }

  const tags = sortedTags(store().tags);
  const chosen = tags.filter((tag) => draft.tagIds.includes(tag.id));
  const unchosen = tags.filter((tag) => !draft.tagIds.includes(tag.id));

  const { dropping, handlers } = usePhotoDrop((files) => {
    void addFiles(files);
  });

  return (
    <Page className="page-column">
      <form
        class={dropping ? "drop-target" : undefined}
        onSubmit={(event) => {
          // Enter in the name field means done, the same as the back arrow.
          event.preventDefault();
          save({});
          go(listPath());
        }}
        {...handlers}
      >
        <div class="person-top">
          <div class="person-fields">
            <div class="row">
              <input
                ref={nameField}
                type="text"
                placeholder="Name"
                aria-label="Name"
                maxLength={80}
                value={draft.name}
                onInput={(event) => {
                  type({ name: event.currentTarget.value });
                }}
                // Asked for by hand, because a browser never submits a form
                // with a phone field besides this one and no submit button
                // on Enter by itself.
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    required(event.currentTarget.form, "form").requestSubmit();
                  }
                }}
                onChange={(event) => {
                  save({ name: event.currentTarget.value });
                }}
              />
            </div>
            <ToucanPhotoRow
              photos={draft.photos}
              onChange={(photos) => {
                save({ photos });
              }}
            />
          </div>
          {/* Your own QR is for capture time; it would confuse an edit of
              someone else. */}
          {initial === null && <MyQr />}
        </div>
        {hint !== "" && <p class="muted form-photos-hint">{hint}</p>}
        {/* Only the tags this person has are chips, pressed, and a tap takes
            one off; the rest wait in the menu, so a library of many tags
            costs the form one line. */}
        <ToucanChipRow class="form-tags">
          {chosen.map((tag) => (
            <ToucanChip
              key={tag.id}
              pressed
              onClick={() => {
                const tagIds = [...draft.tagIds];
                toggleId(tagIds, tag.id);
                save({ tagIds });
              }}
            >
              {tagLabel(tag)}
            </ToucanChip>
          ))}
          <ToucanChipMenu
            label="Add a tag"
            neutral
            items={[
              ...unchosen.map((tag) => ({
                key: tag.id,
                label: tag.name,
                icon: tag.emoji,
                onPick: () => {
                  save({ tagIds: [...latest().tagIds, tag.id] });
                },
              })),
              {
                key: "new",
                label: "New tag…",
                onPick: () => {
                  setNewTag(true);
                },
              },
            ]}
          >
            + Tag
          </ToucanChipMenu>
        </ToucanChipRow>
        <textarea
          placeholder="What did you talk about? Links and contact details welcome."
          aria-label="Note"
          rows={3}
          value={draft.note}
          onInput={(event) => {
            type({ note: event.currentTarget.value });
          }}
          onChange={(event) => {
            save({ note: event.currentTarget.value });
          }}
        />
        {/* A number of their own rather than a line in the note, so the phone
            can be handed the person as a contact. Typed by them as often as
            by you, so it is under the note they are watching. */}
        <div class="row person-phone-row">
          <input
            type="tel"
            placeholder="Phone"
            aria-label="Phone"
            autocomplete="off"
            value={draft.phone}
            onInput={(event) => {
              type({ phone: event.currentTarget.value });
            }}
            onChange={(event) => {
              save({ phone: event.currentTarget.value });
            }}
          />
          {/* Ghosted until there is somebody to hand over — a name makes
              them — rather than absent, so the way into the phone's own
              contacts is on the form to be seen before it is needed. On
              Android it is a real link to the card, which the phone's
              download manager hands to Contacts (see the platform's
              `contact-card.ts` for why nothing shorter works there). */}
          {person !== null && isAndroid() ? (
            <a
              class={toucanPill}
              data-small="true"
              aria-label={ADD_TO_CONTACTS}
              href={contactLink(person)}
              download={contactCardFileName(card(person))}
            >
              📇 {ADD_TO_CONTACTS}
            </a>
          ) : (
            <ToucanButton
              small
              label={ADD_TO_CONTACTS}
              disabled={person === null}
              onClick={() => {
                // jsdom clicks a disabled button, though a browser never does.
                if (person !== null) {
                  void saveToContacts(person);
                }
              }}
            >
              📇 {ADD_TO_CONTACTS}
            </ToucanButton>
          )}
        </div>
        {person !== null && <DangerZone person={person} />}
      </form>
      {newTag && (
        <ToucanNewThingDialog
          thing="tag"
          placeholder="Kind of event — Choir, Python Meetup, book club…"
          taken={takenNames(tags)}
          onSave={(tagName, emoji) => {
            const tag = addTag(
              store(),
              tagName,
              emoji === "" ? undefined : emoji,
            );
            save({ tagIds: [...latest().tagIds, tag.id] });
          }}
          onClose={() => {
            setNewTag(false);
          }}
        />
      )}
    </Page>
  );
}
